import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const InquiryContext = createContext(null);

export const InquiryProvider = ({ children }) => {
  const [inquiryItems, setInquiryItems] = useState(() => {
    // Load from localStorage on initial mount
    const savedItems = localStorage.getItem('inquiryItems');
    return savedItems ? JSON.parse(savedItems) : [];
  });
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);

  // Save to localStorage whenever items change
  useEffect(() => {
    localStorage.setItem('inquiryItems', JSON.stringify(inquiryItems));
  }, [inquiryItems]);

  // Add product to inquiry
  const addToInquiry = useCallback((product) => {
    setInquiryItems((prevItems) => {
      const parsedQty = parseInt(product.quantity, 10);
      const initialQty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
      const merged = { ...product, quantity: initialQty };
      const exists = prevItems.some((item) => item.id === product.id);
      if (exists) {
        return prevItems.map((item) =>
          item.id === product.id ? { ...item, ...merged } : item
        );
      }
      return [...prevItems, { ...merged, addedAt: new Date().toISOString() }];
    });
  }, []);

  // Remove product from inquiry
  const removeFromInquiry = useCallback((productId) => {
    setInquiryItems(prevItems => prevItems.filter(item => item.id !== productId));
  }, []);

  // Update quantity for a product
  const updateQuantity = useCallback((productId, quantity) => {
    setInquiryItems(prevItems => 
      prevItems.map(item => 
        item.id === productId ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  }, []);

  // Clear all inquiry items
  const clearInquiry = useCallback(() => {
    setInquiryItems([]);
  }, []);

  // Check if product is in inquiry
  const isInInquiry = useCallback((productId) => {
    return inquiryItems.some(item => item.id === productId);
  }, [inquiryItems]);

  // Get inquiry count
  const getInquiryCount = useCallback(() => {
    return inquiryItems.length;
  }, [inquiryItems]);

  // Group items by seller
  const getItemsBySeller = useCallback(() => {
    const grouped = {};
    inquiryItems.forEach(item => {
      const sellerId = item.sellerId || item.seller?.id || 'unknown';
      // Use sellerName first, then companyName, then seller.name
      const sellerName = item.sellerName || item.companyName || item.seller?.name || 'Unknown Seller';
      if (!grouped[sellerId]) {
        grouped[sellerId] = {
          sellerId,
          sellerName,
          sellerAvatar: item.sellerAvatar || item.seller?.logo,
          items: []
        };
      }
      grouped[sellerId].items.push(item);
    });
    return Object.values(grouped);
  }, [inquiryItems]);

  // Toggle inquiry sidebar
  const toggleInquiry = useCallback(() => {
    setIsInquiryOpen(prev => !prev);
  }, []);

  // Open inquiry sidebar
  const openInquiry = useCallback(() => {
    setIsInquiryOpen(true);
  }, []);

  // Close inquiry sidebar
  const closeInquiry = useCallback(() => {
    setIsInquiryOpen(false);
  }, []);

  const value = {
    inquiryItems,
    isInquiryOpen,
    addToInquiry,
    removeFromInquiry,
    updateQuantity,
    clearInquiry,
    isInInquiry,
    getInquiryCount,
    getItemsBySeller,
    toggleInquiry,
    openInquiry,
    closeInquiry
  };

  return (
    <InquiryContext.Provider value={value}>
      {children}
    </InquiryContext.Provider>
  );
};

export const useInquiry = () => {
  const context = useContext(InquiryContext);
  if (!context) {
    throw new Error('useInquiry must be used within an InquiryProvider');
  }
  return context;
};

export default InquiryContext;
